import { useLocation, useNavigate } from 'react-router-dom';
import { Globe } from 'lucide-react';
import { LANGUAGES, splitLanguage, pathForLanguage, translator, LanguageCode } from '../utils/i18n';

/**
 * Switches language by navigating to the same page under the other language's
 * prefix, rather than swapping text in place. Each language therefore has its
 * own URL, which is what lets the translated pages be indexed at all and what
 * the hreflang annotations point at.
 */
export default function LanguageSwitcher({ compact = false }: { compact?: boolean }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { lang } = splitLanguage(location.pathname);
    const t = translator(lang);

    // The navbar renders one switcher for desktop and one for mobile, so the
    // id has to differ between them or the label points at the wrong control.
    const selectId = compact ? 'language-select-compact' : 'language-select';

    const change = (next: LanguageCode) => {
        if (next === lang) return;
        navigate(pathForLanguage(location.pathname, next));
    };

    return (
        <div className={`flex items-center ${compact ? 'gap-1' : 'gap-2'}`}>
            <Globe
                className={`${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-slate-500 dark:text-gray-400`}
                aria-hidden="true"
            />
            <label htmlFor={selectId} className="sr-only">
                {t('lang.label', 'Language')}
            </label>
            <select
                id={selectId}
                value={lang}
                onChange={(e) => change(e.target.value as LanguageCode)}
                className={`bg-transparent text-slate-600 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 rounded cursor-pointer ${
                    compact ? 'text-xs py-0.5' : 'text-sm py-1'
                }`}
            >
                {LANGUAGES.map((l) => (
                    <option key={l.code} value={l.code} className="text-slate-900">
                        {l.name}
                    </option>
                ))}
            </select>
        </div>
    );
}
